"use client"

import { useEffect, useState } from "react"
import { Award } from "lucide-react"

interface Achievement {
  id: string
  code: string
  name: string
  description: string
  icon: string
  rarity: string
  points: number
}

interface AchievementToastProps {
  achievements: Achievement[]
  onDismiss: () => void
}

export function AchievementToast({ achievements, onDismiss }: AchievementToastProps) {
  const [visible, setVisible] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (achievements.length === 0) return

    setCurrentIndex(0)
    setVisible(true)

    const timer = setTimeout(() => {
      setVisible(false)
      setTimeout(() => {
        if (currentIndex + 1 < achievements.length) {
          setCurrentIndex((prev) => prev + 1)
          setVisible(true)
        } else {
          onDismiss()
        }
      }, 300)
    }, 4000)

    return () => clearTimeout(timer)
  }, [achievements, onDismiss])

  // Show next achievement when current one dismisses
  useEffect(() => {
    if (!visible && currentIndex > 0 && currentIndex < achievements.length) {
      const timer = setTimeout(() => {
        setVisible(true)
        const hideTimer = setTimeout(() => {
          setVisible(false)
          setTimeout(() => {
            if (currentIndex + 1 < achievements.length) {
              setCurrentIndex((prev) => prev + 1)
            } else {
              onDismiss()
            }
          }, 300)
        }, 4000)
        return () => clearTimeout(hideTimer)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [visible, currentIndex, achievements.length, onDismiss])

  if (achievements.length === 0 || currentIndex >= achievements.length) return null

  const achievement = achievements[currentIndex]
  return (
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-[70] transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
      }`}
    >
      <div
        className="flex items-center gap-3 rounded-2xl border border-[#171717]/15 bg-[#fbf7ef] px-5 py-3 text-[#171717] shadow-[7px_7px_0_#ff6f61]"
        onClick={() => {
          setVisible(false)
          setTimeout(onDismiss, 300)
        }}
      >
        <div className="text-2xl">{achievement.icon}</div>
        <div>
          <div className="flex items-center gap-2">
            <Award className="w-3.5 h-3.5 text-[#b83f34]" />
            <span className="text-xs font-bold uppercase tracking-wide text-[#b83f34]">
              {achievement.rarity} achievement
            </span>
          </div>
          <div className="font-bold text-[#171717]">{achievement.name}</div>
          <div className="text-xs text-[#655d54]">{achievement.description}</div>
        </div>
        <div className="text-sm font-bold text-[#b83f34] ml-2">
          +{achievement.points}
        </div>
      </div>
    </div>
  )
}
